<template>
  <div v-if="showInternalLinks" :class="$style.container">
    <template v-if="isPlatformIs24()">
      <InternalLinkCollapsible
        v-for="{ title, links } in seoLinks"
        :key="title"
        :title="title"
        :links="links"
      />
    </template>
    <template v-else>
      <InternalLinkList
        v-for="{ title, links } in seoLinks"
        :key="title"
        :title="title"
        :links="links"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { type Locale as GeoLocale, type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { computed, onServerPrefetch, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  fetchGeoLocationsByIds,
  getGeoLocationPolygons,
  getMunicipalitiesOfCanton,
  getSurroundingLocationsByPolygon,
} from '@/api/geoService';
import InternalLinkCollapsible from '@/components/internal-links/InternalLinkCollapsible.vue';
import InternalLinkList from '@/components/internal-links/InternalLinkList.vue';
import { isPlatformIs24 } from '@/helpers/platform';
import { getSEORoutes } from '@/router';
import useGeoStore, { type Locale, type LocalizedUrls } from '@/stores/useGeoStore';
import { MapGeoLevel } from '@/types';

const TOP_10_CITIES = [
  'geo-municipality-zuerich',
  'geo-municipality-genf',
  'geo-municipality-basel',
  'geo-municipality-lausanne',
  'geo-municipality-bern',
  'geo-municipality-winterthur',
  'geo-municipality-luzern',
  'geo-municipality-st-gallen',
  'geo-municipality-lugano',
  'geo-municipality-biel-be',
];

const ALL_CANTONS = [
  'geo-canton-zuerich',
  'geo-canton-bern',
  'geo-canton-luzern',
  'geo-canton-uri',
  'geo-canton-schwyz',
  'geo-canton-obwalden',
  'geo-canton-nidwalden',
  'geo-canton-glarus',
  'geo-canton-zug',
  'geo-canton-freiburg',
  'geo-canton-solothurn',
  'geo-canton-basel-stadt',
  'geo-canton-basel-landschaft',
  'geo-canton-schaffhausen',
  'geo-canton-appenzell-ausserrhoden',
  'geo-canton-appenzell-innerrhoden',
  'geo-canton-st-gallen',
  'geo-canton-graubuenden',
  'geo-canton-aargau',
  'geo-canton-thurgau',
  'geo-canton-tessin',
  'geo-canton-waadt',
  'geo-canton-wallis',
  'geo-canton-neuenburg',
  'geo-canton-genf',
  'geo-canton-jura',
];

const { t, locale } = useI18n();
const geoStore = useGeoStore();
const {
  getIaziLevelByGeoType,
  searchLocation,
  localizedSEOLinksGroupTwo,
  localizedSEOLinksGroupOne,
} = storeToRefs(geoStore);

const getTitle = computed((): { groupOneTitle: string; groupTwoTitle: string } => {
  const searchLocationName = searchLocation.value?.names?.[locale.value as GeoLocale][0];
  switch (getIaziLevelByGeoType.value) {
    case MapGeoLevel.Country:
      return {
        groupOneTitle: t('internalLink.title.allCantons'),
        groupTwoTitle: t('internalLink.title.chTopCities'),
      };
    case MapGeoLevel.Canton:
      return {
        groupOneTitle: t('internalLink.title.citiesOfCanton', [searchLocationName]),
        groupTwoTitle: t('internalLink.title.allCantons'),
      };
    case MapGeoLevel.Municipality:
      return {
        groupOneTitle: t('internalLink.title.neighboursCities', [searchLocationName]),
        groupTwoTitle: t('internalLink.title.cantonTopCities'),
      };
    default:
      return { groupOneTitle: '', groupTwoTitle: '' };
  }
});

const seoLinks = computed(
  (): {
    title: string;
    links: Record<Locale, LocalizedUrls>;
  }[] => [
    {
      title: getTitle.value.groupOneTitle,
      links: localizedSEOLinksGroupOne.value ?? {},
    },
    {
      title: getTitle.value.groupTwoTitle,
      links: localizedSEOLinksGroupTwo.value ?? {},
    },
  ],
);

const showInternalLinks = computed((): boolean => getIaziLevelByGeoType.value !== MapGeoLevel.Town);

async function generateSEOLinks() {
  if (getIaziLevelByGeoType.value === MapGeoLevel.Town) {
    geoStore.resetLocalizedSEOLinks();
    return;
  }

  /**
   * Country level
   * 1. All cantons
   * 2. Top 10 cities
   */
  if (getIaziLevelByGeoType.value === MapGeoLevel.Country) {
    const [cantonUrls, topBiggestCitiesUrls] = await Promise.all([
      generateGeoTagsSEOLinks(ALL_CANTONS),
      generateGeoTagsSEOLinks(TOP_10_CITIES),
    ]);
    geoStore.setLocalizeSEOLinks(cantonUrls, 'one');
    geoStore.setLocalizeSEOLinks(topBiggestCitiesUrls, 'two');
    return;
  }

  /**
   * Canton level
   * 1. Cities of canton
   * 2. All cantons
   */
  if (getIaziLevelByGeoType.value === MapGeoLevel.Canton) {
    const currentLocationId = searchLocation.value?.id ?? '';
    const [cityUrls, cantonUrls] = await Promise.all([
      generateMunicipalitiesOfCantonSEOLinks(currentLocationId),
      generateGeoTagsSEOLinks(ALL_CANTONS),
    ]);
    geoStore.setLocalizeSEOLinks(cityUrls, 'one');
    geoStore.setLocalizeSEOLinks(cantonUrls, 'two');
    return;
  }

  /**
   * Municipality level
   * 1. 5 surrounding cities
   * 2. Top 10 cities of canton
   */
  if (getIaziLevelByGeoType.value === MapGeoLevel.Municipality) {
    const [_, cantonGeoId] = searchLocation.value?.parentPaths?.[0] || [];
    const [surroundingCityUrls, top10City] = await Promise.all([
      generateNeighborMunicipalitiesSEOLinks(searchLocation.value!),
      generateMunicipalitiesOfCantonSEOLinks(cantonGeoId, 10),
    ]);
    geoStore.setLocalizeSEOLinks(surroundingCityUrls, 'one');
    geoStore.setLocalizeSEOLinks(top10City, 'two');
  }
}

onServerPrefetch(async () => {
  await generateSEOLinks();
});

watch(searchLocation, async () => {
  geoStore.resetLocalizedSEOLinks();
  await generateSEOLinks();
});

const generateGeoTagsSEOLinks = async (
  geoTags: string[],
): Promise<Record<Locale, LocalizedUrls>> => {
  const allCantons = await fetchGeoLocationsByIds(geoTags);
  return getSEORoutes(allCantons);
};

const generateMunicipalitiesOfCantonSEOLinks = async (
  cantonGeoId: string,
  top?: number,
): Promise<Record<Locale, LocalizedUrls>> => {
  const citiesOfCanton = await getMunicipalitiesOfCanton(cantonGeoId);

  return top
    ? getSEORoutes(citiesOfCanton?.sort((a, b) => (b.area ?? 0) - (a.area ?? 0)).slice(0, top))
    : getSEORoutes(citiesOfCanton);
};

const generateNeighborMunicipalitiesSEOLinks = async (
  currentLocation: GeoLocation,
): Promise<Record<Locale, LocalizedUrls>> => {
  const polygons = await getGeoLocationPolygons(currentLocation.id);
  const neighborMunicipalities = await getSurroundingLocationsByPolygon(
    currentLocation.id,
    'municipality',
    polygons,
  );

  return getSEORoutes(neighborMunicipalities);
};
</script>

<style module lang="scss">
@use '@/styles/variables' as *;

@mixin heading {
  margin-top: 0;
  margin-bottom: 0;

  @include heading-3;
}

.container {
  display: flex;
  flex-direction: column;
  gap: $space-150;

  h3 {
    @include heading;
  }
}

.contentWrapper,
.contentWrapper > [class^='Collapsible_body'] {
  display: flex;
  flex-wrap: wrap;
  column-gap: $space-50;
}

.collapsibleTitle {
  @include heading;
}

.title {
  @include heading;

  & {
    margin-bottom: $space-25;
  }
}
</style>
